<template lang="pug">
  .product-list(
    v-element-visibility="(isVisible) => show(isVisible)"
    :key="podId + uri + widget"
    :class="size ? 'img-size-'+size : []"
    :data-cnstrc-recommendations="isRecommendations || undefined"
    :data-cnstrc-recommendations-pod-id="isRecommendations ? podId : undefined"
    :data-cnstrc-recommendations-seed-items="isRecommendations && itemIds?.length ? itemIds.join(',') : undefined"
    :data-cnstrc-browse="groupId ? true : undefined"
    :data-cnstrc-filter-name="groupId ? 'group_id' : undefined"
    :data-cnstrc-filter-value="groupId || undefined"
    :data-cnstrc-result-id="cnstrcResultId || undefined"
    :data-cnstrc-num-results="cnstrcNumResults"
  )
    .product-card(
      v-for="(product, index) in productList"
      :data-cnstrc-item="isRecommendations ? 'recommendation' : undefined"
      :data-cnstrc-item-id="isRecommendations ? product.productId : undefined"
      :data-cnstrc-item-name="isRecommendations ? product.name : undefined"
      :data-cnstrc-item-price="isRecommendations ? (product.special_price && product.price ? Math.min(product.special_price, product.price) : product.price || undefined) : undefined"
      :data-cnstrc-item-variation-id="isRecommendations ? (product.variationKey || product.childId || undefined) : undefined"
      :data-cnstrc-strategy-id="isRecommendations ? product.strategyId : undefined"
    )
      component.product-link(
        :is="product.url ? 'RogueLink' : 'div'"
        :to="product.url"
        :productId="product.hasRedirect ? '' : product.productId"
        :key="product.productId"
        @click="trackClick(product, index)"
        v-element-visibility="(isVisible) => trackViewProduct(index, isVisible)"
      )
        .product-img
          StaticImage(
            :src="product.thumbnail"
            :width="small ? 83 : 308"
            alt=""
            lazy
            fit-width
          )
          .snipe-img(v-if="listPromos[product.productId]")
            StaticImage.img(v-if="listPromos[product.productId].hasPromo" src="/UI/logos/Hot_Deals_Category_Snipe_ktmfzq.svg" :width="163" :height="44" :alt="__('Hot Deal')")
            StaticImage.img.hundo(v-else-if="listPromos[product.productId].hasHundo" src="/UI/logos/Hundo_Category_Snipe_s7bwil.svg" :width="224" :height="44" :alt="__('Hundo Pricing')")
        .product-name(:class="$slots.product && 'pad-b'")
          .title {{product.name || ' '}}
          hotdeal(v-if="displayHotDeals" :productId="product.productId" layout="carousel")
      slot(name="product" :product="product")
</template>
<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { computed, type ComputedRef, inject, onServerPrefetch, ref, watch } from 'vue'
import type { CatalogFilter, CatalogProduct, Rogue } from '../../js/sisuTypes'
import { useRogue } from '../../js/rogue'

const CATEGORY_WIDGET = 'rfkid_10'

// For analytics
const LIST_TYPES = {
  rfkid_32 : 'forYou',
  rfkid_31 : 'similar',
  rfkid_4  : 'cart',
  rfkid_10 : 'category',
}

// From App.vue
const activePromos = inject<ComputedRef<{ [productId: number]: any }>>('activePromos', computed(() => ({})))
const hundoProductIds = inject<ComputedRef<string[]>>('hundoProductIds', computed(() => []))
const refreshPromos = inject<((rogue: Rogue) => Promise<void>)>('refreshPromos', async () => {})

const rogue = useRogue()

const props = withDefaults(defineProps<{
  lazy?: boolean
  // Used for the sidecart images
  small?: boolean
  products?: CatalogProduct[] | null
  size?: string | null
  displayHotDeals?: boolean
  category?: string | null
  widget?: string
  filter?: CatalogFilter
  podId?: string
  itemIds?: string[]
  filterGroupIds?: string[]
  groupId?: string
}>(), {
  lazy            : false,
  small           : false,
  products        : null,
  size            : null,
  displayHotDeals : false,
  category        : null,
  widget          : 'rfkid_32',
})

const podId = computed(() => props.podId ?? (props.groupId ? undefined : 'preview-search'))

const results = ref<any[]>([{}, {}, {}, {}])
const visible = ref(false)
const loaded = ref(false)
const cnstrcResultId = ref<string | undefined>(undefined)
let loading: boolean = false
let trackOnLoad: number[] | undefined = undefined

const isRecommendations = computed(() => !props.products && !props.groupId)
const cnstrcNumResults = computed<number | undefined>(() => isRecommendations.value && loaded.value ? productList.value.length : undefined)

const productList = computed(() => {
  // only filter on the homepage
  if (rogue.router?.currentRoute.value.path === '/' && props.category) {
    if(props.category.includes('hot-deals')) {
      const productIdsWithPromo = results.value
        .filter(result => listPromos.value[result.productId]?.hasPromo)
        .map(result => result.productId)

      return results.value.filter(result => productIdsWithPromo.includes(result.productId))
    } else if(props.category.includes('hundo')) {
      const productIdsWithPromo = results.value
        .filter(result => listPromos.value[result.productId]?.hasHundo)
        .map(result => result.productId)

      return results.value.filter(result => productIdsWithPromo.includes(result.productId))
    }

  }

  return results.value
})

const uri = computed(() => {
  if (props.category && rogue?.site?.code) {
    let url = props.category
    const code = rogue.site.code
    if (code === 'AUS') {
      url = url.replace('/featured', '/featured-au')
    } else if (code === 'EUR' || code === 'GBR') {
      url = url.replace('/featured', '/featured-eu')
    } else if (url !== '/featured') {
      url = url.replace('/featured', '/na-featured')
    }

    return url
  }

  return rogue.router?.currentRoute.value.fullPath
})

const listPromos = computed(() => {
  if (rogue && loaded.value) {
    const retval = {}
    const promos = activePromos.value
    const hundo = hundoProductIds.value

    results.value.forEach(value => {
      retval[value.productId] = { hasHundo: hundo.includes(`${value.productId}`), hasPromo: (promos[value.productId] || {}).status === 'active' }
    })

    return retval
  }

  return {}
})

const trackData = computed(() => {
  if (props.category) {
    return { listType: 'category', listEntity: { type: 'category', name: 'preview', url: props.category, id: 0 } }
  }

  return { listType: LIST_TYPES[props.widget] }
})

function trackViewProduct(index: number, isVisible?: boolean) {
  if (!isVisible) return

  // If we haven't loaded yet, we need to wait for that first
  if (!loaded.value) {
    trackOnLoad = trackOnLoad || []
    trackOnLoad.push(index)

    return
  }
  const product = results.value[index]
  if (product && index !== undefined && ((rogue || {}).track || {}).listItemViewDebounce) {
    const data = Object.assign({
      type        : 'listView',
      rfkProducts : [{ product, index: index + 1 }],
    }, trackData.value)
    rogue.track.listItemViewDebounce(data)
  }
}

function trackClick(product: any, index: number) {
  if (product && index !== undefined && ((rogue || {}).track || {}).track) {
    const data = Object.assign({
      product,
      type     : 'productClick',
      position : index + 1,
    }, trackData.value)
    rogue.track.track(data)
  }
}

async function load() {
  // If the parent is controlling products (even if empty for a moment), do not fetch RFK
  if (props.products?.length) {
    results.value = props.products
    loaded.value = true

    return
  }
  if (props.products !== null) {
    return
  }

  if (loaded.value || (props.lazy && !visible.value) || !rogue || loading) {
    return
  }

  loading = true

  try {
    const useConstructor = rogue.featureFlags?.isOn('enable_constructor')

    let request: any

    if (useConstructor && podId.value) {
      const filters = props.filterGroupIds?.length ? { group_id: props.filterGroupIds } : undefined
      request = rogue.cnstrc.getRecommendations(podId.value, 12, undefined, props.itemIds, filters)
    } else if (useConstructor) {
      request = rogue.cnstrc.getBrowseResults('group_id', String(props.groupId!), { numResults: 12 })
    } else {
      const payload: any = { page_number: 1, uri: uri.value, n_item: 12, widget: props.widget }
      payload.uri = uri.value
      payload.widget = props.category ? CATEGORY_WIDGET : props.widget
      if (props.filter) {
        payload.filter = props.filter
      }
      if (props.category) {
        payload.sort = { value: [{ type: 'featured', order: 'desc' }] }
      }

      request = rogue.rfk?.fetch(payload)
    }

    const response = request?.then ? await request : request

    results.value = (((response?.content || {}).product || {}).value || [])

    if (useConstructor) {
      cnstrcResultId.value = response?.result_id
    }
  } catch {
    results.value = []
  }

  loaded.value = true

  // If the customer is already viewing this list, we may need to send "View" events
  // now that we are loaded
  if (trackOnLoad) {
    trackOnLoad.forEach(index => trackViewProduct(index))
    trackOnLoad = undefined
  }
}

async function show(isVisible) {
  if (!isVisible) return

  visible.value = isVisible
  if (!loaded.value) {
    await load()
  }
}

watch(() => props.products, (val) => {
  if (val && val.length) {
    results.value = val
    loaded.value = true
  }
}, {
  immediate : true,
  deep      : true,
})

watch(() => props.filterGroupIds, () => {
  loaded.value = false
  loading = false
  results.value = [{}, {}, {}, {}]
  load()
}, { deep: true })

onServerPrefetch(async () => {
  await refreshPromos(rogue)
  await load()
})

if (!rogue.ssr) {
  load()
}
</script>
<style lang="scss" scoped>
.product-list {
  display: flex;
}

.product-name {
  font-family: $font-condensed;
  margin-top: $space-s;
  color: $color-black-light;

  &.pad-b {
    margin-bottom: $space-xxs;
  }

  .title {
    min-height: 38px;
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.product-card {
  margin-right: $space-s;
  flex-shrink: 0;
  flex-grow: 0;
  width: 308px;
  &:last-child {
    margin-right: 0;
  }
}

.product-link {
  text-decoration: none;
}

.product-img {
  position:relative;
}

.img-size-medium {
  .product-card {
    width: 250px;
  }
  @include mobile(){
    .product-card {
      width: 145px;
    }
  }
}

.img-size-compact {
  .product-card {
    width: 150px;
  }
}

.img-size-small-category {
  .product-card {
    @include all-desktop() {
      width: 245px;
    }
    @include mobile() {
      width: 100%;
    }
  }
}

.snipe-img {
  position: absolute;
  bottom: 0;
  width:100%;

  .img {
    width: 50%;
    &.hundo {
      width: 62%;
    }
  }
}

@include mobile() {
  .reactive .product-card {
    width: 125px;
  }
}

.small {
  .product-name {
    font-size: $size-xxs;
    margin-top: $space-xs;
    font-family: $font-base;

    .title {
      min-height: 28px;
    }
  }

  .product-card {
    width: 83px;
  }
}

</style>
